import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { orderBy } from 'lodash';
import { Progress, Modal, Tooltip, Checkbox } from 'antd';
import {
  getAlertColorCode,
  renderBooleanValue
} from '../../../../../util/miscellaneousUtils';
import {
  RadioButttonWithIcons,
  alertRadioOptions
} from '../../../../../components/ns/RadioButttonWithIcons';
import styles from './index.scss';
import { checkAccess } from '../../../../common/utils/index';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCommentAltLines,
  faEllipsisH
} from '@fortawesome/pro-regular-svg-icons';

const resourceCode = 'APP_3_RESOURCE_UI_AMLS_COMMON_ALERTS';

export const NSAlertDetailHits = ({
  GS,
  data,
  apiActions,
  authentication,
  totalDiff,
  selectedForCompare,
  onSelectForComparision
}) => {
  const [isModalVisible, setModalVibility] = useState(false);
  const [selectedHitId, setSelectedHitId] = useState(null);
  const setHitId = (hitId) => {
    setSelectedHitId(hitId);
    setModalVibility(true);
  };

  const renderDataByMultipleKey = (column, record, elementType) => {
    let keys = column.itemName.split('|');
    if (keys.length >= 2 && record !== undefined) {
      let renderCount = 0;
      return keys.map((key, index) => {
        if (record[key]) renderCount = 1;
        if (renderCount === 1) {
          return elementType === 'TD' ? (
            <td key={index}>{record[key] ? <p>{record[key]}</p> : null}</td>
          ) : (
            <p key={index}>{record[key] ? record[key] : null}</p>
          );
        } else if (renderCount === 0 && index === keys.length - 1)
          return elementType === 'TD' ? (
            <td key={index}>{record[key] ? <p>{record[key]}</p> : '--'}</td>
          ) : (
            <p key={index}>{record[key] ? record[key] : '--'}</p>
          );
      });
    } else return null;
  };

  const onUserFeedbackChangeHandler = (obj, record) => {
    const param = {
      alertId: data.alertId,
      alertSourceId: data.alertSourceId,
      watchlistId: record.watchlistId,
      newAlertStatus: obj,
      hitId: record.hitId
    };
    apiActions.postNSHitsUserFeedback(param);
  };

  const addToCompare = (hitId) => {
    onSelectForComparision(hitId);
  };

  const renderCustomTableColumn = (column, record) => {
    const { isWhitelisted: isWhitelistedGlobally = false } = data;
    switch (column?.itemName) {
      case 'isWhitelisted':
        return (
          <td key={`v-${record.alertId}`}>
            {isWhitelistedGlobally == true
              ? ' True (Global) '
              : renderBooleanValue(record[column?.itemName])}
          </td>
        );
      case 'reviewFeedback':
        return (
          <td key={record.hitId}>
            {checkAccess(authentication, resourceCode, 'Update') ? (
              <RadioButttonWithIcons
                options={alertRadioOptions}
                defaultValue={record[column.itemName]?.toString()}
                onChangeHandler={(res) =>
                  onUserFeedbackChangeHandler(res, record)
                }
              />
            ) : (
              '--'
            )}
          </td>
        );
      case 'hitScore':
        return (
          <>
            <div className={styles.flex} key={record.alertId}>
              <div>
                <span>{record.hitLevel}</span>
                <br />
                <Tooltip title={record[column.itemName]}>
                  <span>{record[column.itemName]?.toFixed(2).toString()}</span>
                </Tooltip>
                {record.hitScore && (
                  <Progress
                    percent={record.hitScore}
                    strokeColor={getAlertColorCode(record.hitLevel)}
                    showInfo={false}
                  />
                )}
              </div>
            </div>
            <Checkbox
              style={{ marginRight: '15px' }}
              checked={selectedForCompare.some((s) => s === record.hitId)}
              onChange={() => addToCompare(record.hitId)}
            />
          </>
        );
      case 'furtherInformation':
        return (
          <td key={record.alertId}>
            {record.furtherInformation ? (
              <div className={styles.furtherInformation} key={record.hitId}>
                <div>
                  <FontAwesomeIcon
                    icon={faEllipsisH}
                    style={{ fontSize: '11px', marginRight: '6px' }}
                  />
                  <FontAwesomeIcon
                    icon={faCommentAltLines}
                    style={{ fontSize: '20px' }}
                    onClick={() => {
                      setHitId(record.hitId);
                    }}
                  />
                </div>
                {record.furtherInformation && selectedHitId == record.hitId && (
                  <Modal
                    wrapClassName={styles.alertModalClass}
                    title="Further Information"
                    visible={isModalVisible}
                    width="735px"
                    footer={null}
                    onCancel={() => {
                      setModalVibility(false);
                    }}>
                    {typeof record.furtherInformation === 'string' ? (
                      // Attempt to parse furtherInformation as JSON
                      (() => {
                        try {
                          const obj = JSON.parse(record.furtherInformation);
                          // If parsing is successful, render key-value pairs
                          return (
                            <div style={{ display: 'flex' }}>
                              <div>
                                {Object.entries(obj).map(([key, value]) => (
                                  <div
                                    key={key}
                                    style={{
                                      marginBottom: '12px'
                                    }}>
                                    <span
                                      style={{
                                        fontSize: '14px',
                                        marginBottom: '8px',
                                        textDecoration: 'underline',
                                        textTransform: 'capitalize',
                                        marginRight: '8px'
                                      }}>
                                      {key}:
                                    </span>
                                    <span
                                      style={{
                                        fontWeight: 'bold',
                                        fontSize: '14px',
                                        textTransform: 'capitalize'
                                      }}>
                                      {Array.isArray(value)
                                        ? value.join(', ') // Join array elements with commas
                                        : value
                                        ? value
                                        : '-'}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        } catch (error) {
                          // If JSON parsing fails, render the string as is
                          return <div>{record.furtherInformation}</div>;
                        }
                      })()
                    ) : (
                      // Render string value if furtherInformation is not a string
                      <div>{record.furtherInformation}</div>
                    )}
                  </Modal>
                )}
              </div>
            ) : (
              '--'
            )}
          </td>
        );
    }
    return null;
  };

  const getNestedTableColumns = (columnDetail, rowData, TYPE) => {
    return orderBy(columnDetail, ['itemIndex'], ['asc'])
      .filter((column) => column.visibility)
      .map((column, idx) => {
        if (TYPE === 'HEADING') {
          return <p key={idx}>{column.itemLabel}</p>;
        } else if (
          (column.itemName === 'hitLevel' || column.itemName === 'hitScore') &&
          rowData.hits !== undefined
        )
          return null;
        else if (
          renderCustomTableColumn(column, rowData) ||
          column.itemName === 'hitLevel'
        )
          return renderCustomTableColumn(column, rowData);
        else if (renderDataByMultipleKey(column, rowData))
          return renderDataByMultipleKey(column, rowData);
        else {
          return (
            <Tooltip
              title={
                (column.itemName.toLowerCase().includes('score') ||
                  column.itemName.toLowerCase().includes('weight')) &&
                (typeof rowData[column.itemName] === 'number'
                  ? rowData[column.itemName]?.toFixed(2).toString()
                  : rowData[column.itemName])
              }>
              <p
                key={idx}
                style={{
                  color:
                    (rowData[column.itemName] == 100 ||
                      rowData[column.itemName] == 1) &&
                    column.itemName.toLowerCase().includes('score')
                      ? '#DF6057'
                      : '#556069',
                  ...(columnDetail?.length > 2 && { flexBasis: '46px' }),
                  textAlign: 'start',
                  wordBreak: 'break-all'
                }}>
                {rowData[column.itemName] || rowData[column.itemName] === 0
                  ? typeof rowData[column.itemName] === 'number'
                    ? rowData[column.itemName]?.toFixed(2).toString()
                    : rowData[column.itemName]
                  : '--'}
              </p>
            </Tooltip>
          );
        }
      });
  };

  const getAlertViewHitsHeading = (GS) => {
    let renderHeading = null;
    const sortedSettings = orderBy(
      GS.widgetConfig,
      ['columnIndex'],
      ['asc']
    ).filter((column) => column.columnVisibility);

    renderHeading = sortedSettings.map((setting, id) => {
      return (
        <React.Fragment>
          {setting.columnDetail.length === 0 ? (
            <div className={styles.separatorBar}>{setting.columnName}</div>
          ) : (
            <tr key={id}>
              {setting.columnDetail.length >= 2 ? (
                <React.Fragment>
                  <th className={styles.spaceBetween} key={id}>
                    {getNestedTableColumns(
                      setting.columnDetail,
                      undefined,
                      'HEADING'
                    )}
                  </th>
                  <th className={styles.spaceBetween} key={id + 1}>
                    {getNestedTableColumns(setting.columnDetail, data, 'DATA')}
                  </th>
                </React.Fragment>
              ) : (
                orderBy(setting.columnDetail, ['itemIndex'], ['asc'])
                  .filter((column) => column.visibility)
                  .map((column, idx) => {
                    return (
                      <React.Fragment key={idx}>
                        <th className={styles.bold}> {column.itemLabel} </th>
                        <th>
                          {renderDataByMultipleKey(column, data)
                            ? renderDataByMultipleKey(column, data)
                            : data[column.itemName]
                            ? data[column.itemName]
                            : column.itemName === 'reviewFeedback'
                            ? ''
                            : '--'}
                        </th>
                      </React.Fragment>
                    );
                  })
              )}
            </tr>
          )}
        </React.Fragment>
      );
    });

    return renderHeading;
  };

  const getAlertViewHitsData = (GS) => {
    let renderData = null;
    const sortedSettings = orderBy(
      GS.widgetConfig,
      ['columnIndex'],
      ['asc']
    ).filter((column) => column.columnVisibility);

    renderData = data?.hits?.map((item, itemId) => {
      return (
        <div
          key={itemId}
          className={`${
            selectedForCompare.some((s) => s === item.hitId)
              ? styles.selected
              : ''
          }`}>
          {sortedSettings.map((setting, id) => {
            const isDiffValue = totalDiff.some(
              (s) =>
                setting.columnDetail.some((c) =>
                  c.itemName.split('|').includes(s)
                ) && selectedForCompare.some((s) => s === item.hitId)
            );
            return (
              <tr
                key={id}
                className={`${isDiffValue ? 'diff' : ''} ${
                  setting.columnDetail.length === 0 ? styles.seperator : ''
                }`}>
                {orderBy(setting?.columnDetail, ['itemIndex'], ['asc'])
                  .filter((column) => column.visibility)
                  .map((column, idx) => {
                    if (setting.columnDetail.length >= 2) {
                      if (column.itemIndex === 0)
                        return (
                          <td className={styles.spaceBetween} key={idx}>
                            {getNestedTableColumns(
                              setting.columnDetail,
                              item,
                              'DATA'
                            )}
                          </td>
                        );
                      else return null;
                    } else if (renderCustomTableColumn(column, item) !== null)
                      return renderCustomTableColumn(column, item);
                    else if (
                      renderDataByMultipleKey(column, item, 'TD') !== null
                    )
                      return renderDataByMultipleKey(column, item, 'TD');
                    else {
                      return (
                        <td key={idx}>
                          {item[column.itemName] ? item[column.itemName] : '--'}
                        </td>
                      );
                    }
                  })}
              </tr>
            );
          })}
        </div>
      );
    });

    return renderData;
  };

  if (GS?.isVisible)
    return (
      <React.Fragment>
        <div className={styles.partyIdentity}>
          <table className={styles.hitsTable}>
            <thead>{getAlertViewHitsHeading(GS)}</thead>
            <tbody>{getAlertViewHitsData(GS)}</tbody>
          </table>
          <p className={styles.hints}>
            <b>*</b> These fields are only applicable for individuals
          </p>
        </div>
      </React.Fragment>
    );
  else return null;
};

NSAlertDetailHits.propTypes = {
  GS: PropTypes.object,
  data: PropTypes.object,
  apiActions: PropTypes.object
};

export default NSAlertDetailHits;
